import { Component, Mixins, Ref } from "vue-property-decorator";

import VModal from "@/components/VModal.vue";
import { AutomaticReportForm } from "@/components/Agencies/ReportBuilder";
import {
  AutomaticReportFormValue,
  automaticReportFormInitialState,
  automaticReportToFormValue,
  automaticToTemplateGranularity,
  buildAutomaticReportParametersForTemplate,
  buildAutomaticReportParametersWithInlineTemplate,
  describeAutomaticReportLink,
  isAutomaticReportFormValid,
  GRANULARITY_KIND_LABELS,
} from "@/components/Agencies/ReportBuilder/automaticReport";
import {
  FULL_FORMAT_GRANULARITIES,
  Granularity,
  RAW_FORMAT_GRANULARITIES,
  granularityLabelFor,
} from "@/components/Agencies/ReportBuilder/granularity";
import { ModalAction } from "@/components/modalActionTypes";
import { AUTOMATIC_REPORTS_ENABLED } from "@/constants";
import GenerateFromTemplateMixin from "@/mixins/ReportBuilder/GenerateFromTemplateMixin";
import { ConfirmActionKey } from "@/mixins/ReportBuilder/ReportTemplateCrudMixin";
import { ReportBuilderTabKey } from "@/mixins/ReportBuilder/ReportBuilderMixin";
import AutomaticReportMixin from "@/mixins/http/AutomaticReportMixin";
import { AutomaticReport, ReportTemplate } from "@/models";
import { sanitizeReportName } from "@/services/ReportNameHelper";
import ToastInvoker, { responseErrorMessage } from "@/utils/ToastInvoker";

type TemplateConflictActionKey = "editRecurrence" | "chooseAnotherTemplate";
type ScheduleConfirmActionKey =
  | "saveTemplate"
  | "saveChanges"
  | "scheduleWithoutSaving"
  | "cancel";

@Component
export default class AutomaticReportSchedulingMixin extends Mixins(
  GenerateFromTemplateMixin,
  AutomaticReportMixin
) {
  @Ref("automaticReportPane")
  readonly automaticReportPane?: AutomaticReportForm;
  @Ref("scheduleConfirmModal") readonly scheduleConfirmModal?: VModal;
  @Ref("scheduleNameModal") readonly scheduleNameModal?: VModal;
  @Ref("useTemplateForAutomaticModal")
  readonly useTemplateForAutomaticModal?: VModal;
  @Ref("automaticTemplateConflictModal")
  readonly automaticTemplateConflictModal?: VModal;
  @Ref("sharedTemplateConflictModal")
  readonly sharedTemplateConflictModal?: VModal;

  automaticReportForm: AutomaticReportFormValue =
    automaticReportFormInitialState();
  schedulingAutomaticReport = false;
  editingAutomaticReportId: string | null = null;
  editingAutomaticReportActive = true;
  automaticTemplateConflict: "granularity" | "compare" | null = null;
  mismatchTemplateGranularityLabel = "";
  mismatchGranularityKindLabel = "";
  sharedTemplateConflictAutomaticReportLines: string[] = [];

  get isAutomaticReportValid(): boolean {
    return isAutomaticReportFormValid(this.automaticReportForm);
  }

  get primaryActionsDisabled(): boolean {
    return this.isAutomaticTab
      ? !this.isAutomaticReportValid
      : !this.isBroadcastersStepEnabled;
  }

  get isTemplateFlow(): boolean {
    return (
      this.isEditingTemplate ||
      this.creatingTemplate ||
      this.schedulingAutomaticReport
    );
  }

  get granularities(): Granularity[] {
    const raw = this.outputType === "raw";
    const options = raw ? RAW_FORMAT_GRANULARITIES : FULL_FORMAT_GRANULARITIES;

    if (this.schedulingAutomaticReport) {
      const target = automaticToTemplateGranularity(
        this.automaticReportForm.granularityKind,
        this.automaticReportForm.weekType
      );
      return options.filter((g) => g.id === target);
    }

    if (this.isTemplateFlow) return options;
    return this.granularityOptionsForDays(this.getSelectedDays(), raw);
  }

  get granularityToggleDisabled(): boolean {
    const isRaw = this.parameters.output_type === "raw";
    const blockedByTabularDetail =
      !isRaw && !!this.parameters.special_initiatives_detail;
    const blockedByVodDevices = this.parameters.show_vod_devices && !isRaw;
    const blockedByAutomaticCompare =
      this.schedulingAutomaticReport && this.automaticReportForm.compare;
    return (
      blockedByTabularDetail || blockedByVodDevices || blockedByAutomaticCompare
    );
  }

  onUseTemplateClick() {
    if (this.isAutomaticTab) {
      this.useTemplateForAutomaticModal?.show();
      return;
    }
    this.useTemplateModal?.show();
  }

  closeUseTemplateForAutomaticModal() {
    this.useTemplateForAutomaticModal?.hide();
  }

  get useTemplateForAutomaticModalActions(): ModalAction[] {
    return [
      {
        key: "close",
        label: "Chiudi",
        variant: "secondary",
        run: () => this.closeUseTemplateForAutomaticModal(),
      },
    ];
  }

  onSelectTemplateForAutomatic(template: ReportTemplate) {
    this.useTemplateForAutomaticModal?.hide();
    const competitor = template.competitor;
    const templateHasTemporalDetail = competitor?.temporalDetail === true;

    if (templateHasTemporalDetail && this.automaticReportForm.compare) {
      this.automaticTemplateConflict = "compare";
      this.automaticTemplateConflictModal?.show();
      return;
    }

    const expected = automaticToTemplateGranularity(
      this.automaticReportForm.granularityKind,
      this.automaticReportForm.weekType
    );
    if (templateHasTemporalDetail && competitor?.granularity !== expected) {
      this.mismatchTemplateGranularityLabel = granularityLabelFor(
        competitor?.granularity ?? null
      );
      this.mismatchGranularityKindLabel =
        this.resolveAutomaticReportGranularityKindLabel(
          this.automaticReportForm
        );
      this.automaticTemplateConflict = "granularity";
      this.automaticTemplateConflictModal?.show();
      return;
    }

    this.createAutomaticReportForExistingTemplate(template);
  }

  resolveAutomaticReportGranularityKindLabel(
    automaticReportForm: AutomaticReportFormValue
  ) {
    let label = GRANULARITY_KIND_LABELS[automaticReportForm.granularityKind];
    if (label === "Settimane")
      label +=
        automaticReportForm.weekType === "sun_sat"
          ? " (Dom-Sab)"
          : " (Lun-Dom)";
    return label;
  }

  get automaticTemplateConflictTitle(): string {
    return this.automaticTemplateConflict === "granularity"
      ? "Dettaglio temporale diverso"
      : "Periodo di confronto";
  }

  private dismissAutomaticTemplateConflict() {
    this.automaticTemplateConflictModal?.hide();
    this.automaticTemplateConflict = null;
  }

  get automaticTemplateConflictModalActions(): ModalAction<TemplateConflictActionKey>[] {
    return [
      {
        key: "editRecurrence",
        label: "Modifica la ricorrenza",
        variant: "secondary",
        run: () => {
          this.dismissAutomaticTemplateConflict();
          this.useTemplateForAutomaticModal?.hide();
        },
      },
      {
        key: "chooseAnotherTemplate",
        label: "Scegli un altro template",
        variant: "primary",
        run: () => {
          this.dismissAutomaticTemplateConflict();
          this.useTemplateForAutomaticModal?.show();
        },
      },
    ];
  }

  private async createAutomaticReportForExistingTemplate(
    template: ReportTemplate
  ) {
    if (this.savingTemplate) return;
    if (!template.competitor) {
      ToastInvoker.errorToast("Template senza configurazione.", null);
      return;
    }
    const reportTemplateId = Number(template.id);
    if (!Number.isInteger(reportTemplateId) || reportTemplateId <= 0) {
      ToastInvoker.errorToast("Template non valido.", null);
      return;
    }
    const editing = this.editingAutomaticReportId !== null;

    this.savingTemplate = true;
    try {
      const params = buildAutomaticReportParametersForTemplate(
        this.automaticReportForm,
        reportTemplateId,
        editing ? { active: this.editingAutomaticReportActive } : {}
      );
      if (editing) {
        await this.updateAutomaticReport(
          this.profile.slug,
          this.editingAutomaticReportId as string,
          params
        );
      } else {
        await this.createAutomaticReport(this.profile.slug, params);
      }
      this.invalidateTemplatesCache();
      this.invalidateAutomaticReportsCache();
      this.reset();
      if (editing) {
        this.finishAutomaticScheduling();
        this.automaticReportForm = automaticReportFormInitialState();
        await this.templateActionModal?.dialog("Report automatico aggiornato.");
        this.goBackToAutomaticReportsList();
      } else {
        this.resetAutomaticReportFormAfterCreate();
        await this.templateActionModal?.dialog("Report automatico schedulato.");
      }
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
      this.useTemplateForAutomaticModal?.show();
    } finally {
      this.savingTemplate = false;
    }
  }

  onPrimaryForward() {
    if (this.isAutomaticTab) {
      this.startAutomaticScheduling();
    } else {
      this.onForward();
    }
  }

  startAutomaticScheduling() {
    if (this.editingAutomaticReportId === null) {
      this.exitEditMode();
      this.resetBroadcasterStep();
      this.resetSubjectStep();
      this.resetOutputStep();
    }
    this.schedulingAutomaticReport = true;
    this.activeTab = ReportBuilderTabKey.SingleReport;
    this.currentStep = "broadcasters";
    if (this.automaticReportForm.compare) {
      this.parameters.granularity_enabled = false;
    }
  }

  cancelAutomaticScheduling() {
    const wasEditing = this.editingAutomaticReportId !== null;
    this.schedulingAutomaticReport = false;
    this.scheduleTemplateName = "";
    this.exitEditMode();
    this.exitAutomaticEditMode();
    if (wasEditing) {
      this.automaticReportForm = automaticReportFormInitialState();
      this.goBackToAutomaticReportsList();
      return;
    }
    this.activeTab = ReportBuilderTabKey.AutomaticReport;
  }

  finishAutomaticScheduling() {
    this.schedulingAutomaticReport = false;
    this.scheduleTemplateName = "";
    this.exitEditMode();
    this.exitAutomaticEditMode();
    this.activeTab = ReportBuilderTabKey.AutomaticReport;
  }

  private resetAutomaticReportFormAfterCreate() {
    this.automaticReportForm = { ...this.automaticReportForm, name: "" };
    this.$nextTick(() => this.automaticReportPane?.resetValidation());
  }

  exitAutomaticEditMode() {
    this.editingAutomaticReportId = null;
    this.editingAutomaticReportActive = true;
  }

  async onAutomaticReportEdit(automaticReportId: string): Promise<void> {
    this.loadingTemplate = true;
    try {
      let automaticReport: AutomaticReport | undefined;
      try {
        automaticReport = await this.showAutomaticReport(
          this.profile.slug,
          automaticReportId
        );
      } catch (error) {
        ToastInvoker.errorToast(responseErrorMessage(error), error);
        return;
      }
      if (!automaticReport) return;

      const templateId = automaticReport.reportTemplate?.id;
      if (templateId == null) {
        ToastInvoker.errorToast(
          "Report automatico senza configurazione.",
          null
        );
        return;
      }

      let template: ReportTemplate | undefined;
      try {
        template = await this.showReportTemplate(
          this.profile.slug,
          String(templateId)
        );
      } catch (error) {
        ToastInvoker.errorToast(responseErrorMessage(error), error);
        return;
      }
      if (!template) return;

      const editedAutomaticReportId = String(automaticReport.id);
      const sharedWithAutomaticReports = (
        template.automaticReports ?? []
      ).filter(
        (linkedAutomaticReport) =>
          String(linkedAutomaticReport.id) !== editedAutomaticReportId
      );
      if (sharedWithAutomaticReports.length > 0) {
        this.sharedTemplateConflictAutomaticReportLines =
          sharedWithAutomaticReports.map(describeAutomaticReportLink);
        this.sharedTemplateConflictModal?.show();
        return;
      }

      this.restoringTemplate = true;
      this.applyTemplateToParameters(template);
      this.automaticReportForm = automaticReportToFormValue(automaticReport);
      this.editingAutomaticReportId = automaticReportId;
      this.editingAutomaticReportActive = automaticReport.active ?? true;
      this.activeTab = ReportBuilderTabKey.AutomaticReport;
      this.$nextTick(() => {
        this.restoringTemplate = false;
      });
    } finally {
      this.loadingTemplate = false;
    }
  }

  async updateAutomaticReportFromWizard(
    publicTemplateName: string | null = null
  ): Promise<void> {
    if (this.editingAutomaticReportId === null) return;
    const competitorAttributes = this.validCompetitorAttributes();
    if (!competitorAttributes) return;
    this.savingTemplate = true;
    try {
      await this.updateAutomaticReport(
        this.profile.slug,
        this.editingAutomaticReportId,
        buildAutomaticReportParametersWithInlineTemplate(
          this.automaticReportForm,
          competitorAttributes,
          {
            publicTemplateName,
            active: this.editingAutomaticReportActive,
          }
        )
      );
      this.scheduleNameModal?.hide();
      this.scheduleConfirmModal?.hide();
      this.invalidateTemplatesCache();
      this.invalidateAutomaticReportsCache();
      this.finishAutomaticScheduling();
      this.reset();
      this.automaticReportForm = automaticReportFormInitialState();
      await this.templateActionModal?.dialog("Report automatico aggiornato.");
      this.goBackToAutomaticReportsList();
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
    } finally {
      this.savingTemplate = false;
    }
  }

  closeSharedTemplateConflict() {
    this.sharedTemplateConflictModal?.hide();
    this.sharedTemplateConflictAutomaticReportLines = [];
    this.goBackToAutomaticReportsList();
  }

  get sharedTemplateConflictModalActions(): ModalAction[] {
    return [
      {
        key: "acknowledge",
        label: "Ho capito",
        variant: "primary",
        run: () => this.closeSharedTemplateConflict(),
      },
    ];
  }

  protected goBackToAutomaticReportsList() {
    const query: Record<string, string> = {};
    if (this.profile?.slug) query.profile = this.profile.slug;
    if (!AUTOMATIC_REPORTS_ENABLED) {
      this.$router.push({ name: "Dashboard" });
      return;
    }
    this.$router.push({ name: "SettingsAutomaticReports", query });
  }

  get scheduleConfirmTitle(): string {
    return this.editingAutomaticReportId
      ? "Conferma modifiche"
      : "Conferma schedulazione";
  }

  get scheduleSaveTemplateTitle(): string {
    return this.editingAutomaticReportId
      ? "Salva ed estrai template"
      : "Salva e schedula";
  }

  openScheduleConfirmModal() {
    if (!this.validCompetitorAttributes()) return;
    this.scheduleConfirmModal?.show();
  }

  closeScheduleConfirm() {
    this.scheduleConfirmModal?.hide();
  }

  get scheduleConfirmModalActions(): ModalAction<ScheduleConfirmActionKey>[] {
    const cancel: ModalAction<ScheduleConfirmActionKey> = {
      key: "cancel",
      label: "Annulla",
      variant: "secondary",
      disabled: this.savingTemplate,
      run: () => this.closeScheduleConfirm(),
    };
    const saveTemplate: ModalAction<ScheduleConfirmActionKey> = {
      key: "saveTemplate",
      label: this.scheduleSaveTemplateTitle,
      variant: "outline-primary",
      disabled: this.savingTemplate,
      run: () => this.onScheduleSaveTemplate(),
    };

    if (this.editingAutomaticReportId) {
      return [
        saveTemplate,
        {
          key: "saveChanges",
          label: this.savingTemplate ? "Salvataggio…" : "Salva modifiche",
          variant: "primary",
          disabled: this.savingTemplate,
          run: () => this.updateAutomaticReportFromWizard(),
        },
        cancel,
      ];
    }
    return [
      saveTemplate,
      {
        key: "scheduleWithoutSaving",
        label: "Non salvare, schedula",
        variant: "outline-secondary",
        disabled: this.savingTemplate,
        run: () => this.onScheduleWithoutSaving(),
      },
      cancel,
    ];
  }

  onScheduleSaveTemplate() {
    this.scheduleConfirmModal?.hide();
    this.scheduleTemplateName = "";
    this.scheduleNameModal?.show();
  }

  closeScheduleNameModal() {
    this.scheduleNameModal?.hide();
  }

  get scheduleNameModalActions(): ModalAction<ConfirmActionKey>[] {
    return [
      {
        key: "cancel",
        label: "Annulla",
        variant: "secondary",
        disabled: this.savingTemplate,
        run: () => this.closeScheduleNameModal(),
      },
      {
        key: "confirm",
        label: this.savingTemplate
          ? "Salvataggio…"
          : this.scheduleSaveTemplateTitle,
        variant: "primary",
        disabled:
          this.savingTemplate ||
          !this.nameValidation(this.scheduleTemplateName).canSave,
        run: () => this.confirmScheduleSaveTemplate(),
      },
    ];
  }

  async confirmScheduleSaveTemplate() {
    if (this.savingTemplate) return;
    const name = sanitizeReportName(this.scheduleTemplateName);
    if (!name) return;
    if (this.editingAutomaticReportId !== null) {
      await this.updateAutomaticReportFromWizard(name);
      return;
    }
    await this.scheduleAutomaticReport({ publicTemplateName: name });
  }

  async onScheduleWithoutSaving() {
    this.scheduleConfirmModal?.hide();
    await this.scheduleAutomaticReport({ publicTemplateName: null });
  }

  private async scheduleAutomaticReport(opts: {
    publicTemplateName: string | null;
  }) {
    const competitorAttributes = this.validCompetitorAttributes();
    if (!competitorAttributes) return;

    this.savingTemplate = true;
    try {
      await this.createAutomaticReport(
        this.profile.slug,
        buildAutomaticReportParametersWithInlineTemplate(
          this.automaticReportForm,
          competitorAttributes,
          { publicTemplateName: opts.publicTemplateName }
        )
      );

      this.scheduleNameModal?.hide();
      this.scheduleConfirmModal?.hide();
      this.invalidateTemplatesCache();
      this.invalidateAutomaticReportsCache();
      this.finishAutomaticScheduling();
      this.reset();
      this.resetAutomaticReportFormAfterCreate();
      await this.templateActionModal?.dialog("Report automatico schedulato.");
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
    } finally {
      this.savingTemplate = false;
    }
  }
}
